import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useSelectedColoc } from '../hooks/useSelectedColoc';
import { contactsService } from '../services/contacts.service';
import {
  ContactRequest,
  ColocAgency,
  CONTACT_STATUS_LABELS,
  CONTACT_ACTION_LABELS,
} from '../types/contact';
import { getErrorMessage } from '../utils/error';
import { formatDate } from '../utils/format';
import '../styles/CandidatsPage.css';
import '../styles/ContactsAgency.css';

/**
 * La LISTE des demandes de contact. Elle liste, et ouvre.
 *
 * Le détail — édition, assignation, relance, journal d'actions — vit sur sa
 * propre page. Il occupait auparavant un panneau de 460 px qui empilait trois
 * formulaires en colonne unique et masquait la moitié des colonnes ici.
 *
 * Restent sur cette page les deux choses qui portent sur l'ENSEMBLE des
 * demandes, et non sur l'une d'elles : la création d'un contact, et les emails
 * mis en copie des agences.
 */
const ContactsPage: React.FC = () => {
  const navigate = useNavigate();
  const selectedColoc = useSelectedColoc();
  const [contacts, setContacts] = useState<ContactRequest[]>([]);
  const [agencies, setAgencies] = useState<ColocAgency[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  // Les clôturés sont masqués par défaut : la liste sert le travail en cours,
  // et une demande clôturée n'appelle plus d'action. Rien n'est supprimé — la
  // case les rappelle à l'écran.
  const [avecClotures, setAvecClotures] = useState(false);
  // Config des emails en copie par agence
  const [showCc, setShowCc] = useState(false);
  const [ccDraft, setCcDraft] = useState<Record<string, string>>({});
  const [ccBusy, setCcBusy] = useState(false);
  // Bascule d'accès agence (id en cours)
  const [accesBusyId, setAccesBusyId] = useState<string | null>(null);
  // Création manuelle d'un contact
  const [showNew, setShowNew] = useState(false);
  const [newName, setNewName] = useState('');
  const [newEmail, setNewEmail] = useState('');
  const [newPhone, setNewPhone] = useState('');
  const [newMessage, setNewMessage] = useState('');
  const [newBusy, setNewBusy] = useState(false);

  const loadData = useCallback(async () => {
    if (!selectedColoc?.id) {
      setContacts([]);
      setAgencies([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const [data, ags] = await Promise.all([
        contactsService.listForColoc(selectedColoc.id),
        contactsService.listColocAgencies(selectedColoc.id),
      ]);
      setContacts(
        [...data].sort((a, b) => (b.dateCreated || '').localeCompare(a.dateCreated || ''))
      );
      setAgencies(ags);
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors du chargement des contacts'));
    } finally {
      setLoading(false);
    }
  }, [selectedColoc]);

  useEffect(() => {
    loadData();
  }, [loadData]);

  /** Gèle ou rétablit l'accès d'une agence. Ce n'est PAS la révocation :
   *  rien n'est désassigné, et le rétablissement retrouve tout en place. */
  const handleToggleAcces = async (agence: ColocAgency) => {
    setAccesBusyId(agence.id);
    setError(null);
    try {
      await contactsService.setAgencyAccess(
        selectedColoc!.id, agence.id, !!agence.suspended,
      );
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de la modification de l'accès"));
    } finally {
      setAccesBusyId(null);
    }
  };

  const openCc = async () => {
    if (showCc) {
      setShowCc(false);
      return;
    }
    setShowCc(true);
    const draft: Record<string, string> = {};
    await Promise.all(
      agencies.map(async (a) => {
        try {
          const list = await contactsService.getCcRecipients(a.id);
          draft[a.id] = list.join('\n');
        } catch {
          draft[a.id] = '';
        }
      })
    );
    setCcDraft(draft);
  };

  const saveCc = async (agencyId: string) => {
    setCcBusy(true);
    setNotice(null);
    try {
      const emails = (ccDraft[agencyId] || '')
        .split(/[\n,;]+/)
        .map((s) => s.trim())
        .filter(Boolean);
      const saved = await contactsService.setCcRecipients(agencyId, emails);
      setCcDraft({ ...ccDraft, [agencyId]: saved.join('\n') });
      setNotice('Emails en copie enregistrés.');
    } catch (err) {
      setError(getErrorMessage(err, "Erreur lors de l'enregistrement des emails en copie"));
    } finally {
      setCcBusy(false);
    }
  };

  const handleCreate = async () => {
    if (!selectedColoc?.id || !newName.trim() || !newEmail.trim()) return;
    setNewBusy(true);
    setNotice(null);
    try {
      await contactsService.createContact(selectedColoc.id, {
        name: newName.trim(),
        email: newEmail.trim(),
        phone: newPhone.trim() || undefined,
        message: newMessage.trim() || undefined,
      });
      setNewName('');
      setNewEmail('');
      setNewPhone('');
      setNewMessage('');
      setShowNew(false);
      setNotice('Contact créé.');
      await loadData();
    } catch (err) {
      setError(getErrorMessage(err, 'Erreur lors de la création du contact'));
    } finally {
      setNewBusy(false);
    }
  };

  const contactsVisibles = avecClotures
    ? contacts
    : contacts.filter(c => c.status !== 'CLOSED');
  const nbClotures = contacts.length - contacts.filter(c => c.status !== 'CLOSED').length;

  if (loading) {
    return <div className="loading">Chargement...</div>;
  }

  return (
    <div className="candidats-page">
      <div className="candidats-header">
        <h2>Demandes de contact</h2>
        <div className="contacts-entete-actions">
          {nbClotures > 0 && (
            <label className="contacts-filtre-clotures">
              <input
                type="checkbox"
                checked={avecClotures}
                onChange={(e) => setAvecClotures(e.target.checked)}
              />
              Afficher les clôturées ({nbClotures})
            </label>
          )}
          <button className="btn-action btn-save" onClick={() => setShowNew(!showNew)}>
            {showNew ? 'Annuler' : '+ Nouveau contact'}
          </button>
        </div>
      </div>

      {error && <div className="candidats-error">{error}</div>}
      {notice && <div className="candidats-notice">{notice}</div>}

      {showNew && (
        <div className="cx-detail" style={{ marginBottom: 16 }}>
          <div className="cx-card">
            <h4 className="cx-title">Nouveau contact</h4>
            <div className="action-form-grid">
              <div className="action-field">
                <label htmlFor="nc-name">Nom *</label>
                <input id="nc-name" type="text" value={newName} onChange={(e) => setNewName(e.target.value)} />
              </div>
              <div className="action-field">
                <label htmlFor="nc-email">Email *</label>
                <input id="nc-email" type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} />
              </div>
              <div className="action-field">
                <label htmlFor="nc-phone">Téléphone</label>
                <input id="nc-phone" type="tel" value={newPhone} onChange={(e) => setNewPhone(e.target.value)} />
              </div>
              <div className="action-field" style={{ gridColumn: '1 / -1' }}>
                <label htmlFor="nc-message">Message / origine</label>
                <textarea
                  id="nc-message"
                  rows={3}
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder="Ex : contact reçu via Instagram…"
                />
              </div>
              <div className="action-actions">
                <button
                  className="btn-action btn-save"
                  disabled={newBusy || !newName.trim() || !newEmail.trim()}
                  onClick={handleCreate}
                >
                  Créer le contact
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {agencies.length > 0 && (
        <div style={{ marginBottom: 16 }}>
          <button className="btn-action btn-secondary" onClick={openCc}>
            {showCc ? 'Masquer les emails en copie' : 'Emails en copie des agences'}
          </button>
          {showCc && (
            <div className="cx-detail" style={{ marginTop: 12 }}>
              {agencies.map((a) => (
                <div key={a.id} className="cx-card">
                  <h4 className="cx-title">
                    {a.name} — emails en copie
                    {a.suspended && (
                      <span className="agence-badge-suspendue">Accès suspendu</span>
                    )}
                  </h4>
                  <p className="access-hint">
                    {a.suspended
                      ? "Cette agence ne voit plus rien de cette colocation. Les contacts qui lui sont assignés l'attendent : le rétablissement les lui rend tels quels."
                      : 'Suspendre gèle l’accès sans rien défaire. Pour le retirer définitivement et récupérer les contacts en cours, il faut le révoquer.'}
                  </p>
                  <div className="action-field">
                    <textarea
                      value={ccDraft[a.id] ?? ''}
                      onChange={(e) => setCcDraft({ ...ccDraft, [a.id]: e.target.value })}
                      placeholder="un email par ligne"
                      rows={4}
                    />
                  </div>
                  <div className="cx-actions" style={{ marginTop: 12 }}>
                    <button
                      className="btn-action btn-save"
                      disabled={ccBusy}
                      onClick={() => saveCc(a.id)}
                    >
                      Enregistrer
                    </button>
                    <button
                      className="btn-action btn-secondary"
                      disabled={accesBusyId === a.id}
                      onClick={() => handleToggleAcces(a)}
                    >
                      {accesBusyId === a.id
                        ? '…'
                        : a.suspended
                          ? "Rétablir l'accès"
                          : "Suspendre l'accès"}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}

      {contactsVisibles.length === 0 ? (
        <div className="socle-vide">
          <p className="socle-vide-titre">
            {contacts.length === 0
              ? 'Aucune demande de contact'
              : 'Aucune demande en cours'}
          </p>
          <p className="socle-vide-aide">
            {contacts.length === 0 ? (
              <>
                Les messages envoyés depuis le formulaire du site public arrivent ici.
                Vous pouvez aussi saisir vous-même un contact reçu par téléphone ou
                par une agence avec « Nouveau contact ».
              </>
            ) : (
              <>
                Toutes vos demandes sont clôturées. Cochez « Afficher les
                clôturées » pour les retrouver.
              </>
            )}
          </p>
        </div>
      ) : (
        <div className="contacts-table-container">
        <table className="candidats-table">
          <thead>
            <tr>
              <th>Nom</th>
              <th>Email</th>
              <th>Dernier contact agence</th>
              <th>Statut</th>
              <th>Agence</th>
              <th>Date</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {contactsVisibles.map((c) => (
              <tr
                key={c.id}
                className="candidats-row"
                onClick={() => navigate(`/contacts/${c.id}`)}
              >
                <td>{c.name}</td>
                <td>{c.email}</td>
                <td>
                  {c.lastActionType ? (
                    <>
                      {CONTACT_ACTION_LABELS[c.lastActionType] || c.lastActionType}
                      {c.lastActionAt ? ` · ${formatDate(c.lastActionAt)}` : ''}
                    </>
                  ) : (
                    <span style={{ color: '#d92d20', fontWeight: 500 }}>aucun</span>
                  )}
                </td>
                <td>{CONTACT_STATUS_LABELS[c.status] || c.status}</td>
                <td>{c.assignedAgencyName || '—'}</td>
                <td>{c.dateCreated ? formatDate(c.dateCreated) : '—'}</td>
                <td className="cx-actions" onClick={(e) => e.stopPropagation()}>
                  <button
                    className="btn-action btn-secondary"
                    onClick={() => navigate(`/contacts/${c.id}`)}
                  >
                    Ouvrir
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        </div>
      )}
    </div>
  );
};

export default ContactsPage;
